import { useTranslation } from 'react-i18next';
import { AutoFloatingPanel } from 'src/designSystem/AutoFloatingPanel';
import { useEffect } from 'react';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { useCurrentUser, useUpdateCurrentUser } from 'src/queries/user';
import { usePaywall } from 'src/contexts/PaywallContext';
import PaywallScreenDescription from 'src/components/Paywall/PaywallScreenDescription';
import { useNotification } from 'src/contexts/NotificationContext';
import Button from 'src/designSystem/Button';
import { usePostHogFeatureFlag } from 'src/hooks/usePostHogFeatureFlag';
import { isChildUser, isParentUser } from 'src/utils/userRole';

const StartTrialPanel = () => {
  const { t } = useTranslation();
  const { mutateAsync: saveCurrentUser, isLoading: isUpdating } =
    useUpdateCurrentUser();
  const { showError } = useNotification();
  const { isPremium } = usePaywall();
  const { value: freeFirstLessonEnabled } = usePostHogFeatureFlag('is_free_first_lesson_enabled');
  const { data: user } = useCurrentUser();
  const isParent = isParentUser(user);

  const hasTrialStarted =
    !!user?.trial_ends_at || !!user?.family?.trial_ends_at;
  const isOpen = Boolean(
    !isPremium && user && user.onboarding_completed && !hasTrialStarted && !freeFirstLessonEnabled,
  );

  useEffect(() => {
    if (isOpen) {
      logAnalyticsEvent('paywall_viewed', {
        type: 'onboarding_screen1_child',
      });
    }
  }, [isOpen]);

  const onContinue = async () => {
    try {
      await saveCurrentUser({
        onboarding_completed: true,
      });
      logAnalyticsEvent('trial_started', {
        is_paid: false,
        duration: isChildUser(user) ? '24h' : '7d',
      });
    } catch (error) {
      showError({
        error,
        message: t('error.http.default'),
      });
    }
  };

  return (
    <AutoFloatingPanel
      data-testid={'panel-start-trial'}
      isOpen={isOpen}
      closable={false}
    >
      <PaywallScreenDescription />
      <Button
        data-testid={'btn-start-trial'}
        type={'primary'}
        onClick={onContinue}
        loading={isUpdating}
      >
        {isParent
          ? t(`paywall.description.parent.button`)
          : t(`paywall.description.child.button`)}
      </Button>
    </AutoFloatingPanel>
  );
};

export default StartTrialPanel;
